/**
 * Permission catalog and role -> permission map.
 * Source: _truth/02-architecture.md §7.2 (RESOURCE_ACTION strings); standards/authorization.md.
 */
export const PERMISSIONS = [
  "TENANT_READ",
  "ROLE_MANAGE",
  "TEAM_READ",
  "TEAM_ACTIVATE",
  "TEAM_DEACTIVATE",
  "TEAM_SCAN",
  "DELEGATE_MANAGE",
  "PARTICIPANT_READ",
  "PARTICIPANT_EDIT",
  "PARTICIPANT_BULK_EDIT",
  "REVIEW_DECIDE",
  "ADHOC_RAISE",
  "DOMAIN_READ",
  "DOMAIN_EDIT",
  "REASON_MANAGE",
  "CAMPAIGN_READ",
  "CAMPAIGN_LAUNCH",
  "CAMPAIGN_MANAGE",
  "REMOVAL_READ",
  "REMOVAL_REQUEST",
  "REMOVAL_VALIDATE",
  "EXCEPTION_READ",
  "EXCEPTION_APPROVE",
  "EXPORT_CREATE",
  "EXPORT_READ",
  "AUDIT_READ",
  "SETTINGS_READ",
  "SETTINGS_EDIT",
  "REMOVAL_MODE_MANAGE",
  "BETA_MANAGE",
  "SUBSCRIPTION_READ",
  "SUBSCRIPTION_MANAGE",
  "LIFECYCLE_PURGE",
  "LIFECYCLE_EXPORT",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/** Roles a caller can hold. `owner`/`delegate` are scoped to a Team; the others to the tenant. */
export const ROLES = ["primary_admin", "admin", "viewer", "owner", "delegate"] as const;
export type Role = (typeof ROLES)[number];

export const TEAM_SCOPED_ROLES: readonly Role[] = ["owner", "delegate"];

const ALL: ReadonlySet<Permission> = new Set(PERMISSIONS);

const VIEWER: readonly Permission[] = [
  "TENANT_READ",
  "TEAM_READ",
  "PARTICIPANT_READ",
  "DOMAIN_READ",
  "CAMPAIGN_READ",
  "REMOVAL_READ",
  "EXCEPTION_READ",
  "EXPORT_CREATE",
  "EXPORT_READ",
  "AUDIT_READ",
  "SETTINGS_READ",
  "SUBSCRIPTION_READ",
];

// Team Owner: no DOMAIN_READ (BR-007: Domains is Admin/Viewer only).
const OWNER: readonly Permission[] = [
  "TEAM_READ",
  "TEAM_ACTIVATE",
  "TEAM_DEACTIVATE",
  "TEAM_SCAN",
  "DELEGATE_MANAGE",
  "PARTICIPANT_READ",
  "PARTICIPANT_EDIT",
  "PARTICIPANT_BULK_EDIT",
  "REVIEW_DECIDE",
  "ADHOC_RAISE",
  "REMOVAL_READ",
  "REMOVAL_REQUEST",
  "EXCEPTION_READ",
  "EXPORT_CREATE",
  "EXPORT_READ",
  "AUDIT_READ",
  "CAMPAIGN_READ",
];

const DELEGATE_EXCLUDED: ReadonlySet<Permission> = new Set([
  "TEAM_ACTIVATE",
  "TEAM_DEACTIVATE",
  "DELEGATE_MANAGE",
]);

export const ROLE_PERMISSIONS: Readonly<Record<Role, ReadonlySet<Permission>>> = {
  primary_admin: ALL,
  admin: new Set(PERMISSIONS.filter((p) => p !== "LIFECYCLE_PURGE")),
  viewer: new Set(VIEWER),
  owner: new Set(OWNER),
  delegate: new Set(OWNER.filter((p) => !DELEGATE_EXCLUDED.has(p))),
};

export interface RoleGrant {
  role: Role;
  /** Required for team-scoped roles: the controlled team the role applies to. */
  teamId?: string;
}

export interface PermissionContext {
  teamId?: string;
}

/**
 * Gate used everywhere (`can(user, perm, { teamId })`): tenant-scoped roles apply to every team,
 * team-scoped roles only to their own team. Without a teamId, a team-scoped role only grants
 * the permission when asked "at all" via `anyTeam` semantics (list endpoints filter in repositories).
 */
export function can(grants: readonly RoleGrant[], perm: Permission, ctx: PermissionContext = {}): boolean {
  for (const g of grants) {
    if (!ROLE_PERMISSIONS[g.role].has(perm)) continue;
    if (!TEAM_SCOPED_ROLES.includes(g.role)) return true;
    if (ctx.teamId === undefined || g.teamId === ctx.teamId) return true;
  }
  return false;
}

/** Teams the caller may act on for a permission; `"all"` when a tenant-scoped role grants it. */
export function allowedTeamIds(grants: readonly RoleGrant[], perm: Permission): "all" | string[] {
  const ids = new Set<string>();
  for (const g of grants) {
    if (!ROLE_PERMISSIONS[g.role].has(perm)) continue;
    if (!TEAM_SCOPED_ROLES.includes(g.role)) return "all";
    if (g.teamId) ids.add(g.teamId);
  }
  return [...ids];
}
